import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Search, Plus, Trash2, Download, FileSpreadsheet, Send, MessageCircle,
    ChevronDown, Cable,
} from "lucide-react";
import { Logo, Eyebrow } from "@/components/Reveal";
import ThemeToggle from "@/components/ThemeToggle";
import Seo from "@/components/Seo";
import { api, API } from "@/lib/api";
import { useEstimate, estimateTotals } from "@/lib/estimate";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const inputCls =
    "w-full min-w-0 bg-white border border-line rounded-md px-4 py-3 text-sm text-ink focus:outline-none focus:border-copper transition-colors duration-300";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5";

const STATES = ["Karnataka", "Andhra Pradesh", "Tamil Nadu", "Kerala", "Telangana", "Maharashtra", "Goa", "Other"];

const BRANDS = ["KEI", "Polycab", "Havells", "Universal"];

export default function PriceList() {
    const est = useEstimate();
    const [data, setData] = useState(null);
    const [settings, setSettings] = useState({ show_lp: false, whatsapp: "919008177176" });
    const [cat, setCat] = useState(null);
    const [brand, setBrand] = useState("");
    const [search, setSearch] = useState("");
    const [dropOpen, setDropOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [qty, setQty] = useState("");
    const [cust, setCust] = useState({ name: "", company: "", phone: "", email: "", state: "Karnataka", gstin: "" });
    const [busy, setBusy] = useState("");
    const [lastEstimate, setLastEstimate] = useState(null);
    const dropRef = useRef(null);

    useEffect(() => {
        api.get("/prices").then((res) => setData(res.data)).catch(() => setData({ categories: [], items: [] }));
        api.get("/prices/settings").then((res) => setSettings(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const close = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    useEffect(() => {
        if (window.location.hash === "#estimate") {
            document.getElementById("estimate")?.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    // Deep link: /prices?sku=CU-AR-16X4C pre-selects that cable (used by the home rate strip)
    useEffect(() => {
        if (!data || !data.items.length) return;
        const sku = new URLSearchParams(window.location.search).get("sku");
        if (!sku) return;
        const it = data.items.find((i) => i.sku === sku.toUpperCase());
        if (!it) return;
        setCat(it.category);
        setSelected(it);
        setSearch(it.size_label || it.description);
        toast.success(`${it.size_label || it.description} — rate loaded`, { description: "Enter quantity and add to your estimate" });
    }, [data]);

    const items = data?.items || [];
    const catItems = useMemo(() => items.filter((i) => i.category === cat), [items, cat]);
    const brands = useMemo(() => ["All", ...new Set(catItems.map((i) => i.brand).filter(Boolean))], [catItems]);
    const visible = useMemo(() => {
        let list = catItems;
        if (search.trim()) {
            const n = search.toLowerCase().replace(/[^a-z0-9.]+/g, "");
            list = list.filter((i) =>
                (i.size_label + " " + i.description + " " + i.sku).toLowerCase().replace(/[^a-z0-9.]+/g, "").includes(n)
            );
        }
        return list;
    }, [catItems, search]);

    const totals = estimateTotals(est.items);
    const intrastate = (cust.state || "").trim().toLowerCase() === "karnataka";

    const pickCat = (slug) => {
        setCat(slug);
        setSelected(null);
        setSearch("");
    };

    const addToEstimate = () => {
        const q = parseFloat(qty);
        if (!selected) return;
        if (!q || q <= 0) {
            toast.error("Enter a valid quantity");
            return;
        }
        if (selected.moq && q < selected.moq) {
            toast.error(`Minimum order quantity is ${selected.moq} ${selected.unit}`);
            return;
        }
        est.add(selected, q, brand);
        toast.success("Added to estimate", { description: `${selected.description}${brand ? ` — ${brand}` : ""} × ${q} ${selected.unit}` });
        setSelected(null);
        setQty("");
        setSearch("");
    };

    const createEstimate = async (withContact) => {
        if (!est.items.length) {
            toast.error("Your estimate is empty");
            return null;
        }
        if (withContact && !cust.phone.trim() && !cust.email.trim()) {
            toast.error("Add your mobile number or email so we can reach you");
            return null;
        }
        try {
            const res = await api.post("/estimates", {
                items: est.items.map((i) => ({ sku: i.sku, qty: i.qty, brand: i.brand || "" })),
                customer: withContact ? cust : { state: cust.state },
            });
            setLastEstimate(res.data);
            return res.data;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not create the estimate — try again");
            return null;
        }
    };

    const download = async (kind) => {
        setBusy(kind);
        /* open the window synchronously (popup blockers kill async window.open) */
        const win = window.open("", "_blank");
        if (win) win.document.write("<p style='font-family:sans-serif;padding:2rem;color:#555'>Preparing your Roxy Cables quotation…</p>");
        const e = await createEstimate(false);
        setBusy("");
        if (e) {
            const url = `${API}/estimates/${e.estimate_no}/${kind}?t=${e.token}`;
            if (win) win.location = url;
            else window.open(url, "_blank", "noopener");
        } else if (win) {
            win.close();
        }
    };

    const sendToRoxy = async () => {
        setBusy("send");
        const e = await createEstimate(true);
        setBusy("");
        if (e) {
            toast.success(`Estimate ${e.estimate_no} sent to Roxy Cables`, {
                description: "Our desk will confirm availability and final pricing on call or email.",
            });
        }
    };

    const sendWhatsApp = async () => {
        setBusy("wa");
        /* pre-open synchronously so popup blockers don't kill it */
        const win = window.open("", "_blank");
        const e = lastEstimate || (await createEstimate(!!(cust.phone.trim() || cust.email.trim())));
        setBusy("");
        if (!e) {
            if (win) win.close();
            return;
        }
        const lines = est.items.map(
            (i) => `• ${i.description}${i.brand ? ` (${i.brand})` : ""} — ${i.qty} ${i.unit} × ₹${i.flat_price} = ₹${(i.flat_price * i.qty).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
        );
        const gstLine = intrastate ? `CGST 9% + SGST 9%: ${inr(e.gst_total)}` : `IGST 18%: ${inr(e.gst_total)}`;
        const msg = [
            `Hello Roxy Cables, estimate ${e.estimate_no}${cust.name ? ` from ${cust.name}` : ""}:`,
            "",
            ...lines,
            "",
            `Subtotal: ${inr(e.subtotal)}`,
            gstLine,
            `Grand Total: ${inr(e.total)}`,
        ].join("\n");
        const url = `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent(msg)}`;
        if (win) win.location = url;
        else window.open(url, "_blank", "noopener");
    };

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="prices-page">
            <Seo
                title="Cable Prices & Instant Estimate"
                description="Browse live cable prices at Roxy Cables, Bengaluru — aluminium armoured, copper armoured and copper flexible cables. Build an instant estimate with GST and download it as PDF or Excel."
                path="/prices"
            />
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-paper/85 border-b border-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={30} />
                        <span className="font-display font-bold tracking-[0.06em] text-lg text-ink hidden sm:block">Roxy Cables</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <ThemeToggle />
                        <Link to="/" data-testid="prices-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                            <ArrowLeft size={14} /> Site
                        </Link>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14">
                <Eyebrow className="mb-5">Cable Prices</Eyebrow>
                <h1 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink mb-4" data-testid="prices-title">
                    Live prices. <em className="text-copper">Instant estimate.</em>
                </h1>
                <p className="text-mist leading-relaxed max-w-2xl mb-12">
                    Pick a category, choose your size, enter the quantity — your estimate with GST is ready
                    to download as PDF or Excel, or send straight to our desk.
                </p>

                {data === null ? (
                    <div className="grid md:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <div key={i} className="h-44 rounded-md border border-line bg-white animate-pulse" />)}</div>
                ) : items.length === 0 ? (
                    <div className="rounded-md border border-line bg-white p-12 text-center" data-testid="prices-empty">
                        <Cable size={28} className="text-copper mx-auto mb-4" />
                        <p className="font-display font-semibold text-2xl text-ink mb-2">Price list being updated</p>
                        <p className="text-mist text-sm">Our live price list is being loaded. Call +91 900 81 77 176 for today's rates.</p>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-[1fr_420px] gap-10 items-start">
                        <div>
                            {/* STEP 1 — category */}
                            <p className={labelCls}>Step 1 — Category</p>
                            <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="price-categories">
                                {data.categories.map((c) => (
                                    <button
                                        key={c.slug}
                                        data-testid={`price-cat-${c.slug}`}
                                        onClick={() => pickCat(c.slug)}
                                        className={`rounded-md border p-5 text-left transition-all duration-300 ${
                                            cat === c.slug ? "border-copper bg-copper-light shadow-md" : "border-line bg-white hover:border-copper/50"
                                        }`}
                                    >
                                        <p className="font-display font-semibold text-base text-ink leading-snug">{c.label}</p>
                                        <p className="font-mono text-[10px] text-dim uppercase tracking-[0.15em] mt-2">
                                            {items.filter((i) => i.category === c.slug).length} sizes
                                        </p>
                                    </button>
                                ))}
                            </div>

                            {cat && (
                                <>
                                    {/* STEP 2 — brand preference */}
                                    <p className={labelCls}>Step 2 — Preferred brand</p>
                                    {(() => {
                                        const eff = data.items.map((i) => i.effective_from).filter(Boolean).sort().pop();
                                        return eff ? (
                                            <p className="font-mono text-[10px] text-dim uppercase tracking-[0.15em] mb-5" data-testid="prices-effective-note">
                                                Prices effective {eff} · indicative until confirmed by Roxy Cables
                                            </p>
                                        ) : null;
                                    })()}
                                    <div className="flex flex-wrap gap-2 mb-8" data-testid="price-brands">
                                        {["", ...BRANDS].map((b) => (
                                            <button
                                                key={b || "any"}
                                                data-testid={`price-brand-${(b || "no-preference").toLowerCase()}`}
                                                onClick={() => setBrand(b)}
                                                className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 border rounded-full transition-colors duration-300 ${
                                                    brand === b ? "bg-ink border-ink text-white" : "border-line text-mist hover:border-copper hover:text-copper"
                                                }`}
                                            >
                                                {b || "No preference"}
                                            </button>
                                        ))}
                                    </div>

                                    {/* STEP 3 — searchable size dropdown */}
                                    <p className={labelCls}>Step 3 — Cable size</p>
                                    <div className="relative mb-8" ref={dropRef}>
                                        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-dim z-10" />
                                        <input
                                            data-testid="price-size-search"
                                            value={search}
                                            onFocus={() => setDropOpen(true)}
                                            onChange={(e) => { setSearch(e.target.value); setDropOpen(true); setSelected(null); }}
                                            placeholder="Type to search — e.g. 25 x 4 Core, 16 sqmm…"
                                            className={`${inputCls} pl-11 pr-10`}
                                        />
                                        <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-dim pointer-events-none" />
                                        {dropOpen && (
                                            <div className="absolute z-20 inset-x-0 top-full mt-2 max-h-72 overflow-y-auto rounded-md border border-line bg-white shadow-xl" data-testid="price-size-list">
                                                {visible.length === 0 ? (
                                                    <p className="px-4 py-4 text-sm text-mist">No sizes match — try another search.</p>
                                                ) : (
                                                    visible.map((i) => (
                                                        <button
                                                            key={i.sku}
                                                            data-testid={`price-option-${i.sku.toLowerCase()}`}
                                                            onClick={() => { setSelected(i); setSearch(i.size_label || i.description); setDropOpen(false); }}
                                                            className="w-full text-left px-4 py-3 hover:bg-copper-light/60 transition-colors duration-150 flex justify-between items-center gap-3 border-b border-line last:border-b-0"
                                                        >
                                                            <span>
                                                                <span className="block text-sm text-ink">{i.description}</span>
                                                                <span className="block font-mono text-[10px] text-dim">{i.sku}{i.brand ? ` · ${i.brand}` : ""}</span>
                                                            </span>
                                                            <span className="font-mono text-xs text-copper whitespace-nowrap">{inr(i.flat_price)}/{i.unit}</span>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* STEP 4-7 — price display + qty + add */}
                                    {selected && (
                                        <div className="rounded-md border border-copper/40 bg-white p-6 mb-8 shadow-sm" data-testid="price-selected-card">
                                            <p className="font-display font-semibold text-lg text-ink mb-1">
                                                {selected.description}
                                                {brand && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.15em] text-copper border border-copper/40 rounded-full px-2.5 py-1 align-middle">{brand}</span>}
                                            </p>
                                            <p className="font-mono text-[10px] text-dim uppercase tracking-[0.15em] mb-4">{selected.sku}{selected.hsn ? ` · HSN ${selected.hsn}` : ""}</p>
                                            <div className="flex flex-wrap items-end gap-6 mb-5">
                                                {settings.show_lp && selected.lp != null && (
                                                    <div>
                                                        <p className={labelCls}>List Price</p>
                                                        <p className="font-mono text-sm text-dim line-through" data-testid="price-lp">{inr(selected.lp)}</p>
                                                    </div>
                                                )}
                                                <div>
                                                    <p className={labelCls}>Our Price</p>
                                                    <p className="font-display font-bold text-3xl text-copper" data-testid="price-flat">
                                                        {inr(selected.flat_price)} <span className="text-sm text-dim font-mono font-normal">/ {selected.unit}</span>
                                                    </p>
                                                </div>
                                                {selected.moq > 1 && <p className="font-mono text-[10px] text-dim uppercase">MOQ {selected.moq} {selected.unit}</p>}
                                                {selected.availability === "confirm" && (
                                                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-amber-600 border border-amber-500/50 bg-amber-500/10 rounded-full px-3 py-1.5" data-testid="price-confirm-availability">
                                                        Confirm availability with our desk
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-end gap-3">
                                                <div className="w-36">
                                                    <label className={labelCls}>Qty ({selected.unit})</label>
                                                    <input
                                                        data-testid="price-qty-input"
                                                        type="number"
                                                        min="0"
                                                        step="any"
                                                        value={qty}
                                                        onChange={(e) => setQty(e.target.value)}
                                                        className={inputCls}
                                                    />
                                                </div>
                                                {parseFloat(qty) > 0 && (
                                                    <p className="font-mono text-sm text-ink pb-3" data-testid="price-line-amount">
                                                        = {inr(selected.flat_price * parseFloat(qty))}
                                                    </p>
                                                )}
                                                <button
                                                    data-testid="price-add-btn"
                                                    onClick={addToEstimate}
                                                    className="rounded-full bg-copper text-white font-mono uppercase tracking-[0.15em] text-[11px] px-7 py-3.5 hover:bg-copper-bright transition-colors duration-300 flex items-center gap-2"
                                                >
                                                    <Plus size={14} /> Add to estimate
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Estimate panel — sticky on desktop */}
                        <div id="estimate" className="lg:sticky lg:top-24 min-w-0 w-full" data-testid="estimate-panel">
                            <div className="rounded-md border border-line bg-white shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-line bg-paper2 flex items-center justify-between">
                                    <p className="font-display font-semibold text-lg text-ink">Your Estimate</p>
                                    {est.count > 0 && (
                                        <button data-testid="estimate-clear" onClick={() => est.clear()} className="font-mono text-[10px] uppercase tracking-[0.15em] text-dim hover:text-copper transition-colors">
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                {est.items.length === 0 ? (
                                    <p className="px-5 py-8 text-sm text-mist" data-testid="estimate-empty">No cables selected yet — pick a category on the left.</p>
                                ) : (
                                    <>
                                        <div className="max-h-72 overflow-y-auto">
                                            {est.items.map((i) => (
                                                <div key={i.key} className="px-5 py-3.5 border-b border-line last:border-b-0 flex items-center gap-3" data-testid={`estimate-item-${i.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-ink truncate">{i.description}</p>
                                                        <p className="font-mono text-[10px] text-dim">
                                                            {inr(i.flat_price)} / {i.unit}{i.brand ? <span className="text-copper"> · {i.brand}</span> : ""}
                                                        </p>
                                                    </div>
                                                    <input
                                                        data-testid={`estimate-qty-${i.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                        type="number"
                                                        min="0"
                                                        step="any"
                                                        value={i.qty}
                                                        onChange={(e) => est.setQty(i.key, parseFloat(e.target.value) || 0)}
                                                        className="w-20 bg-white border border-line rounded-md px-2 py-1.5 text-sm text-ink focus:outline-none focus:border-copper"
                                                    />
                                                    <span className="font-mono text-xs text-ink w-20 text-right">{inr(i.flat_price * i.qty)}</span>
                                                    <button
                                                        data-testid={`estimate-remove-${i.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                        onClick={() => est.remove(i.key)}
                                                        className="text-dim hover:text-red-500 transition-colors"
                                                        aria-label={`Remove ${i.description}`}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="px-5 py-4 border-t border-line space-y-1.5 font-mono text-sm">
                                            <p className="flex justify-between text-mist"><span>SUBTOTAL</span><span className="text-ink" data-testid="estimate-subtotal">{inr(totals.subtotal)}</span></p>
                                            <p className="flex justify-between text-mist">
                                                <span>{(() => { const rates = new Set(est.items.map((i) => i.gst_percent ?? 18)); if (rates.size > 1) return "GST (mixed rates)"; const r = [...rates][0] ?? 18; return intrastate ? `CGST ${r / 2}% + SGST ${r / 2}%` : `IGST ${r}%`; })()}</span>
                                                <span className="text-ink" data-testid="estimate-gst">{inr(totals.gst)}</span>
                                            </p>
                                            <p className="flex justify-between text-ink font-bold text-base border-t border-line pt-2">
                                                <span>GRAND TOTAL</span><span data-testid="estimate-total" className="text-copper">{inr(totals.total)}</span>
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                            {est.items.length > 0 && (
                                <div className="rounded-md border border-line bg-white shadow-sm mt-4 p-5">
                                    <p className={labelCls}>Your details {intrastate ? "" : ""}</p>
                                    <div className="grid grid-cols-2 gap-3 mb-3 [&>*]:min-w-0">
                                        <input data-testid="cust-name" placeholder="Name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} className={inputCls} />
                                        <input data-testid="cust-company" placeholder="Company" value={cust.company} onChange={(e) => setCust({ ...cust, company: e.target.value })} className={inputCls} />
                                        <input data-testid="cust-phone" placeholder="Mobile" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} className={inputCls} />
                                        <input data-testid="cust-email" placeholder="Email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} className={inputCls} />
                                        <select data-testid="cust-state" value={cust.state} onChange={(e) => setCust({ ...cust, state: e.target.value })} className={`${inputCls} appearance-none`}>
                                            {STATES.map((s) => <option key={s}>{s}</option>)}
                                        </select>
                                        <input data-testid="cust-gstin" placeholder="GSTIN (optional)" value={cust.gstin} onChange={(e) => setCust({ ...cust, gstin: e.target.value })} className={inputCls} />
                                    </div>
                                    <div className="grid gap-2">
                                        <button
                                            data-testid="estimate-download-pdf"
                                            onClick={() => download("pdf")}
                                            disabled={!!busy}
                                            className="rounded-full bg-ink text-white font-mono uppercase tracking-[0.15em] text-[11px] py-3.5 hover:bg-copper transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            <Download size={13} /> {busy === "pdf" ? "Preparing…" : "Download Quotation — PDF"}
                                        </button>
                                        <button
                                            data-testid="estimate-download-xlsx"
                                            onClick={() => download("xlsx")}
                                            disabled={!!busy}
                                            className="rounded-full border border-ink/20 text-ink font-mono uppercase tracking-[0.15em] text-[11px] py-3.5 hover:border-copper hover:text-copper transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            <FileSpreadsheet size={13} /> {busy === "xlsx" ? "Preparing…" : "Download Quotation — Excel"}
                                        </button>
                                        <button
                                            data-testid="estimate-send"
                                            onClick={sendToRoxy}
                                            disabled={!!busy}
                                            className="rounded-full bg-copper text-white font-mono uppercase tracking-[0.15em] text-[11px] py-3.5 hover:bg-copper-bright transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            <Send size={13} /> {busy === "send" ? "Sending…" : "Send estimate to Roxy"}
                                        </button>
                                        <button
                                            data-testid="estimate-whatsapp"
                                            onClick={sendWhatsApp}
                                            disabled={!!busy}
                                            className="rounded-full border border-[#25D366]/40 text-[#1da851] font-mono uppercase tracking-[0.15em] text-[11px] py-3.5 hover:bg-[#25D366]/10 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            <MessageCircle size={13} /> Send via WhatsApp
                                        </button>
                                    </div>
                                    {lastEstimate && (
                                        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-dim text-center" data-testid="estimate-ref">
                                            Ref {lastEstimate.estimate_no} · valid 7 days
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
